import React, { useState, useEffect } from 'react';
import { Search, Printer, Image, ZoomIn, X, Loader2, Edit } from 'lucide-react';
import { api, API_BASE } from '../utils/api';
import { getStatusColor, formatDateThai, sortEmployeesByDepartmentAndId, mergeEmployeeAttendance, printReport } from '../utils/helpers';

const DailyReport = ({ user }) => {
  const [reports, setReports] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [organization, setOrganization] = useState(null);
  const [loading, setLoading] = useState(true);
  
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedImage, setSelectedImage] = useState(null);
  const [printLoading, setPrintLoading] = useState(false);

  const [showNoteModal, setShowNoteModal] = useState(false);
  const [selectedRecordForNote, setSelectedRecordForNote] = useState(null);
  const [noteContent, setNoteContent] = useState('');

  const isAdmin = user?.is_admin === true;

  const getImageUrl = (src) => {
    if (!src) {
      return ''; 
    }
    if (src.startsWith('data:image') || src.startsWith('http')) {
      return src;
    }
    return `${API_BASE}${src}`;
  };

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    loadReports();
  }, [selectedDate, selectedEmployee, searchTerm]); 

  useEffect(() => {
    if (employees.length > 0) {
      loadReports();
    }
  }, [employees]);

  const loadInitialData = async () => {
    try {
      // ⚠️ ข้อมูลที่ได้จะถูกกรองเป็นของโรงเรียนตัวเองอัตโนมัติจาก Backend
      const [employeesData, organizationData] = await Promise.all([
        api.getEmployees(),
        api.getOrganizations()
      ]);
      
      const activeEmployees = employeesData.filter(emp => 
        emp.is_active == 1 && 
        emp.employee_id !== 'ADMIN' && 
        emp.position !== 'ผู้ดูแลระบบ'
      );
      
      setEmployees(activeEmployees);
      setOrganization(organizationData[0] || null);
    } catch (error) {
      console.error('Error loading initial data:', error);
    }
  };

  const loadReports = async () => {
    setLoading(true);
    try {
      const params = {
        type: 'daily',
        date_from: selectedDate,
        date_to: selectedDate
      };
      
      if (selectedEmployee) {
        params.employee_id = selectedEmployee;
      }

      // ⚠️ รายงานจะถูกดึงมาเฉพาะของโรงเรียนตัวเองอัตโนมัติ
      const attendanceData = await api.getReports(params);
      
      let filteredEmployees = employees;
      
      if (selectedEmployee) {
        filteredEmployees = employees.filter(emp => emp.employee_id === selectedEmployee);
      }
      
      if (searchTerm) {
        filteredEmployees = filteredEmployees.filter(emp =>
          emp.employee_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
          emp.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          emp.last_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (emp.department && emp.department.toLowerCase().includes(searchTerm.toLowerCase()))
        );
      }

      const sortedEmployees = sortEmployeesByDepartmentAndId(filteredEmployees);
      const mergedData = mergeEmployeeAttendance(sortedEmployees, attendanceData);
      
      const processedData = mergedData.map(record => {
        const isCheckInMissing = !record.check_in || record.check_in === '-';
        
        if (isCheckInMissing && record.status === 'ปกติ') {
          return { ...record, status: 'ไม่ลงเวลาเข้า' };
        }
        return record;
      });
      
      setReports(processedData);

    } catch (error) {
      console.error('Error loading reports:', error);
      setReports([]);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenNoteModal = (record) => {
    setSelectedRecordForNote(record);
    setNoteContent(record.attendance_notes || '');
    setShowNoteModal(true);
  };

  const handleSaveNote = async () => {
    if (!selectedRecordForNote) return;
    const { attendance_id, status, employee_id, work_date } = selectedRecordForNote;
    try {
      await api.updateAttendanceStatus(
        attendance_id, 
        status,       
        noteContent,   
        employee_id,   
        work_date      
      );
      setShowNoteModal(false);
      setSelectedRecordForNote(null);
      setNoteContent('');
      loadReports();
      window.alert('บันทึกหมายเหตุเรียบร้อยแล้ว');
    } catch (error) {
      console.error('Error saving note:', error);
      window.alert('เกิดข้อผิดพลาดในการบันทึก: ' + error.message);
    }
  };

const generatePrintReport = () => {
    if (reports.length === 0) {
      alert('ไม่มีข้อมูลสำหรับพิมพ์');
      return;
    }

    const dateTitle = formatDateThai(selectedDate);
    const logoHtml = organization?.logo 
      ? `<img src="${API_BASE}${organization.logo}" alt="โลโก้หน่วยงาน" class="logo">`
      : '';

    let reportTitle = `รายงานการลงเวลาปฏิบัติราชการขอข้าราชการครูและบุคลากรทางการศึกษา<br>${dateTitle}`;
    
    const normalCount = reports.filter(r => r.status === 'ปกติ').length;
    const lateCount = reports.filter(r => r.status === 'สาย').length;
    const absentCount = reports.filter(r => r.status === 'ไม่มาปฏิบัติงาน').length;
    const leaveCount = reports.filter(r => ['ลาป่วย', 'ลากิจ'].includes(r.status)).length;
    const officialCount = reports.filter(r => r.status === 'ไปราชการ').length;
    const noCheckInCount = reports.filter(r => r.status === 'ไม่ลงเวลาเข้า').length;
    const noCheckOutCount = reports.filter(r => r.status === 'ไม่ลงเวลาออก').length;
    const earlyOutCount = reports.filter(r => r.status === 'ออกก่อนเวลา').length;
    
    let tableHeaders = `
      <tr>
        <th style="width: 25%;">ชื่อ-นามสกุล</th>
        <th style="width: 20%;">กลุ่มงาน/กลุ่มสาระฯ</th>
        <th style="width: 10%; text-align: center;">เวลาเข้า</th>
        <th style="width: 10%; text-align: center;">เวลาออก</th>
        <th style="width: 15%; text-align: center;">สถานะ</th>
        <th style="width: 20%;">หมายเหตุ</th>
      </tr>
    `;
    
    let tableRows = reports.map(record => {
      const shouldHideTime = ['ไปราชการ', 'ไม่มาปฏิบัติงาน'].includes(record.status) || record.status.startsWith('ลา');

      return `
      <tr>
        <td>${record.first_name} ${record.last_name}</td>
        <td>${record.department || '-'}</td>
        <td style="text-align: center;">${shouldHideTime ? '-' : (record.check_in || '-')}</td>
        <td style="text-align: center;">${shouldHideTime ? '-' : (record.check_out || '-')}</td>
        <td style="text-align: center;">${record.status}</td>
        <td>${record.attendance_notes || '-'}</td>
      </tr>
    `}).join('');
    
    const summaryStats = `
      <div class="summary-stats">
        <h3>สรุปผลการลงเวลา</h3>
        <div class="stats-grid">
          <div class="stat-item"><span class="stat-label">ปกติ:</span><span class="stat-value">${normalCount}</span></div>
          <div class="stat-item"><span class="stat-label">สาย:</span><span class="stat-value">${lateCount}</span></div>
          <div class="stat-item"><span class="stat-label">ไม่มาปฏิบัติงาน:</span><span class="stat-value">${absentCount}</span></div>
          <div class="stat-item"><span class="stat-label">ลา:</span><span class="stat-value">${leaveCount}</span></div>
          <div class="stat-item"><span class="stat-label">ไปราชการ:</span><span class="stat-value">${officialCount}</span></div>
          <div class="stat-item"><span class="stat-label">ไม่ลงเวลาเข้า:</span><span class="stat-value">${noCheckInCount}</span></div>
          <div class="stat-item"><span class="stat-label">ไม่ลงเวลาออก:</span><span class="stat-value">${noCheckOutCount}</span></div>
          <div class="stat-item"><span class="stat-label">ออกก่อนเวลา:</span><span class="stat-value">${earlyOutCount}</span></div>
          <div class="stat-item"><span class="stat-label">รวมทั้งหมด:</span><span class="stat-value">${reports.length}</span></div>
        </div>
      </div>
    `;

    const statHeadName = organization?.report_stat_head_name || '........................................';
    const statHeadPos = organization?.report_stat_head_position || 'เจ้าหน้าที่งานสถิติ';
    
    const vpName = organization?.report_vp_name || '........................................';
    const vpPos = organization?.report_vp_position || 'รองผู้อำนวยการกลุ่มบริหารงานบุคคล';

    const signatureSection = `
      <div class="signature-section" style="margin-top: 40px;">
        <table style="width: 100%; border: none;">
          <tr style="border: none;">
            <td style="border: none; text-align: center; width: 50%; vertical-align: top;">
              <p>ลงชื่อ..........................................................</p>
              <p>(${statHeadName})</p>
              <p>${statHeadPos}</p>
            </td>
            <td style="border: none; text-align: center; width: 50%; vertical-align: top;">
              <p>ลงชื่อ..........................................................</p>
              <p>(${vpName})</p>
              <p>${vpPos}</p>
            </td>
          </tr>
          <tr style="border: none;">
             <td colspan="2" style="border: none; text-align: center; padding-top: 20px;">
                วันที่ ${formatDateThai(new Date())}
             </td>
          </tr>
        </table>
      </div>
    `;

    const reportContent = `
      <style>
        @font-face {
          font-family: 'Sarabun';
          src: url('${API_BASE}fonts/THSarabun.ttf') format('truetype');
          font-weight: 300;
          font-style: normal;
        }
        body {
          font-family: 'Sarabun', sans-serif;
          font-size: 18pt;
          color: #000;
        }
        .header {
          font-size: 18pt;
          text-align: center;
          margin-bottom: 20px;
        }
        table {
          width: 100%;
          border-collapse: collapse;
        }
        th, td {
          border: 1px solid #000;
          text-align: left;
          vertical-align: middle;  
          height: auto;       
          padding: 6px 4px;   
          line-height: 1.2;
          word-wrap: break-word;
        }
        th {
          background-color: #f0f0f0;
          font-weight: bold;
        }
        .summary-stats {
          margin-top: 20px;
          border: 1px solid #000;
          padding: 10px;
        }
        .stats-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr); 
          gap: 10px;
        }
        .stat-item {
          display: flex;
          justify-content: space-between;
        }
        @media print {
          body { -webkit-print-color-adjust: exact; }
          .page-break { page-break-before: always; }
        }
      </style>
      
      <div class="header">
        ${logoHtml}
        <div class="org-name">${organization?.name || 'หน่วยงาน'}</div>
        <div class="date">${reportTitle}</div>
      </div>
      
      <table>
        <thead>${tableHeaders}</thead>
        <tbody>${tableRows}</tbody>
      </table>
      
      ${summaryStats}
      
      ${signatureSection} 
    `;
    printReport(reportContent, reportTitle);
  };

  const renderTableHeaders = () => {
    return (
      <tr>
        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">รหัส</th>
        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ชื่อ-นามสกุล</th>
        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">แผนก/กลุ่มสาระฯ</th>
        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ตำแหน่ง</th>
        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">เข้างาน</th>
        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ออกงาน</th>
        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">สถานะ</th>
        {isAdmin && (
          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ภาพเข้า/ออก</th>
        )}
        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">หมายเหตุ</th>
        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">การจัดการ</th>
      </tr>
    );
  };

  const renderTableRow = (record, index) => {
    const shouldHideTime = ['ไปราชการ', 'ไม่มาปฏิบัติงาน'].includes(record.status) || record.status.startsWith('ลา');

    return (
      <tr key={`${record.employee_id}-${index}`} className="hover:bg-gray-50">
        <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
          {record.employee_id}
        </td>
        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
          {record.first_name} {record.last_name}
        </td>
        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
          {record.department || '-'}
        </td>
        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
          {record.academic_position || '-'}
        </td>
        
        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
          {shouldHideTime ? '-' : (record.check_in || '-')}
        </td>
        
        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
          {shouldHideTime ? '-' : (record.check_out || '-')}
        </td>

        <td className="px-4 py-4 whitespace-nowrap">
          <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(record.status)}`}>
            {record.status}
          </span>
        </td>
        {isAdmin && (
          <td className="px-4 py-4 whitespace-nowrap text-center">
            <div className="flex justify-center space-x-2">
              {record.check_in_image && record.check_in_image.trim() !== '' ? (
                <button
                  onClick={() => setSelectedImage({
                    src: record.check_in_image, 
                    employee: `${record.first_name} ${record.last_name}`,
                    time: record.check_in,
                    date: record.work_date,
                    type: 'เข้างาน'
                  })}
                  className="text-green-600 hover:text-green-900 transition-colors"
                  title="ดูภาพเข้างาน"
                >
                  <Image className="h-4 w-4" />
                </button>
              ) : (
                <span className="text-gray-400 text-xs w-4">-</span>
              )}
              
              {record.check_out_image && record.check_out_image.trim() !== '' ? (
                <button
                  onClick={() => setSelectedImage({
                    src: record.check_out_image,
                    employee: `${record.first_name} ${record.last_name}`,
                    time: record.check_out,
                    date: record.work_date,
                    type: 'ออกงาน'
                  })}
                  className="text-blue-600 hover:text-blue-900 transition-colors"
                  title="ดูภาพออกงาน"
                >
                  <Image className="h-4 w-4" />
                </button>
              ) : (
                <span className="text-gray-400 text-xs w-4">-</span>
              )}
            </div>
          </td>
        )}
        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
          {record.attendance_notes || '-'}
        </td>
        <td className="px-4 py-4 whitespace-nowrap text-sm text-center">
          <button
            onClick={() => handleOpenNoteModal(record)}
            className="text-blue-600 hover:text-blue-900 disabled:text-gray-300 disabled:cursor-not-allowed"
            title="แก้ไขหมายเหตุ"
          >
            <Edit className="h-4 w-4" />
          </button>
        </td>
      </tr>
    );
  };

  const getTableColSpan = () => {
    return isAdmin ? 10 : 9;
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center space-y-4 sm:space-y-0">
        <h1 className="text-3xl font-bold text-gray-900">รายงานรายวัน</h1>
        <button
          onClick={generatePrintReport}
          disabled={loading || reports.length === 0 || printLoading || !organization} 
          className="bg-green-500 text-white px-4 py-2 rounded-md hover:bg-green-600 disabled:opacity-50 flex items-center space-x-2 transition-colors"
        >
          {printLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Printer className="h-4 w-4" />}
          <span>พิมพ์รายงาน</span>
        </button>
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6"> 
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">เลือกวันที่</label>
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">บุคลากร</label>
            <select
              value={selectedEmployee}
              onChange={(e) => setSelectedEmployee(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">ทุกคน</option>
              {employees
                .filter(emp => emp.employee_id !== 'ADMIN' && emp.position !== 'ผู้ดูแลระบบ')
                .map(emp => (
                  <option key={emp.employee_id} value={emp.employee_id}>
                    {emp.employee_id} - {emp.first_name} {emp.last_name}
                  </option>
                ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">ค้นหา</label>
            <div className="relative">
              <Search className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="ค้นหาชื่อ, รหัส, แผนก/กลุ่มสาระฯ..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>
        
        {loading ? (
          <div className="flex justify-center py-8">
            <div className="flex flex-col items-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mb-2"></div>
              <p className="text-sm text-gray-500">กำลังโหลดข้อมูล...</p>
            </div>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  {renderTableHeaders()}
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {reports.length > 0 ? reports.map((record, index) => renderTableRow(record, index)) : (
                    <tr>
                      <td colSpan={getTableColSpan()} className="px-4 py-8 text-center text-gray-500">
                        ไม่พบข้อมูลในช่วงที่เลือก
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {reports.length > 0 && (
              <div className="mt-4 text-sm text-gray-600 flex justify-between items-center">
                <span>แสดงผลลัพธ์ {reports.length} รายการ</span>
                <div className="flex flex-wrap gap-4 text-sm">
                    <span>ปกติ: {reports.filter(r => r.status === 'ปกติ').length}</span>
                    <span>สาย: {reports.filter(r => r.status === 'สาย').length}</span>
                    <span>ไม่มาปฏิบัติงาน: {reports.filter(r => r.status === 'ไม่มาปฏิบัติงาน').length}</span>
                    <span>ไปราชการ: {reports.filter(r => r.status === 'ไปราชการ').length}</span>
                    <span>ลา: {reports.filter(r => ['ลาป่วย', 'ลากิจ'].includes(r.status)).length}</span>
                    <span>ไม่ลงเวลาเข้า: {reports.filter(r => r.status === 'ไม่ลงเวลาเข้า').length}</span>
                    <span>ไม่ลงเวลาออก: {reports.filter(r => r.status === 'ไม่ลงเวลาออก').length}</span>
                    <span>ออกก่อนเวลา: {reports.filter(r => r.status === 'ออกก่อนเวลา').length}</span>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {selectedImage && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-4xl max-h-full overflow-auto">
            <div className="flex justify-between items-center mb-4">
              <div>
                <h3 className="text-lg font-semibold">ภาพการลงเวลา{selectedImage.type ? ` (${selectedImage.type})` : ''}</h3>
                <p className="text-sm text-gray-600">
                  {selectedImage.employee} - {selectedImage.time} 
                  {selectedImage.date && ` (${formatDateThai(selectedImage.date)})`}
                </p>
              </div>
              <button
                onClick={() => setSelectedImage(null)}
                className="text-gray-500 hover:text-gray-700 transition-colors"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            <div className="text-center">
              <img
                src={getImageUrl(selectedImage.src)}
                alt="ภาพการลงเวลา"
                className="max-w-full max-h-[70vh] object-contain rounded-lg"
              />
            </div>
          </div>
        </div>
      )}

      {showNoteModal && selectedRecordForNote && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold text-gray-900 mb-4">
              แก้ไขหมายเหตุ
            </h2>
            <p className="text-sm text-gray-600 mb-4">
              สำหรับ: {selectedRecordForNote.first_name} {selectedRecordForNote.last_name}
              <br/>
              วันที่: {formatDateThai(selectedRecordForNote.work_date)}
            </p>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  หมายเหตุ (โดยเจ้าหน้าที่)
                </label>
                <textarea
                  rows="4"
                  value={noteContent}
                  onChange={(e) => setNoteContent(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="เช่น ประชุมผู้ปกครอง, อบรม..."
                ></textarea>
              </div>
            </div>

            <div className="flex space-x-4 mt-8">
              <button
                onClick={handleSaveNote}
                className="flex-1 bg-blue-500 text-white py-3 px-4 rounded-md hover:bg-blue-600 transition-colors"
              >
                บันทึกหมายเหตุ
              </button>
              <button
                onClick={() => setShowNoteModal(false)}
                className="flex-1 bg-gray-300 text-gray-700 py-3 px-4 rounded-md hover:bg-gray-400 transition-colors"
              >
                ยกเลิก
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default DailyReport;